import { useId } from "react";
import {
  evenSplit,
  parseSplitQuantity,
  splitSummary,
  type VersionSplitEntry,
} from "@shared-core/domain/version-split";
import type { QuoteLineView } from "@shared-core/data/customer-link-adapter";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";

// Under the quantity a customer chose on a line of versions that offers several quantities (supplier interface spec 53):
// one whole-number input per version, labelled by its label and name, a "Split evenly" button and the running sum. The
// split is required to accept (Ben, 27 Oct), so the inputs are bound to the backend's refusal like the quantity choice.

function versionName(
  version: QuoteLineView["versions"][number],
  position: number,
): string {
  const label = version.label ?? String.fromCharCode(64 + position);
  return version.name ? `${label} · ${version.name}` : label;
}

export function VersionSplitInputs({
  line,
  total,
  entry,
  error,
  onChange,
}: {
  line: QuoteLineView;
  /** The quantity chosen: what the versions' quantities must add up to. */
  total: number;
  /** One entry per version, in version order; `null` is not given yet. */
  entry: VersionSplitEntry;
  /** The backend's refusal of this line's split, if any. */
  error: string | undefined;
  onChange: (entry: (number | null)[]) => void;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const count = line.versions.length;
  const value = (position: number) => entry[position] ?? null;
  return (
    <div
      role="group"
      aria-label={`Quantity of each version of ${line.title}`}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? errorId : undefined}
      className="space-y-3"
    >
      <div className="space-y-1">
        <p className="text-sm font-medium">
          Give each version's quantity ({total.toLocaleString("en-GB")} in
          total)
        </p>
        <p className="text-sm text-muted-foreground">
          {count} versions. You'll give each version's quantity when you accept.
        </p>
      </div>
      <ul className="space-y-2">
        {line.versions.map((version, position) => (
          <li key={version.position} className="flex items-center gap-3">
            <label
              htmlFor={`${id}-${position}`}
              className="w-40 shrink-0 text-sm"
            >
              {versionName(version, position + 1)}
            </label>
            <Input
              id={`${id}-${position}`}
              inputMode="numeric"
              className="w-32 tabular-nums"
              value={
                value(position) === null
                  ? ""
                  : value(position)!.toLocaleString("en-GB")
              }
              onChange={(e) => {
                const next = Array.from(
                  { length: count },
                  (_, i) => entry[i] ?? null,
                );
                next[position] = parseSplitQuantity(e.target.value);
                onChange(next);
              }}
            />
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange(evenSplit(total, count))}
        >
          Split evenly
        </Button>
        <span
          className="text-sm text-muted-foreground tabular-nums"
          role="status"
        >
          {splitSummary(entry, total)}
        </span>
      </div>
      {error && (
        <p
          id={errorId}
          role="alert"
          className="text-sm text-red-700 dark:text-red-400"
        >
          {error}
        </p>
      )}
    </div>
  );
}
